import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { StyleSpecification } from "maplibre-gl";
import publicStyles from "../../config/styles.json";
import style from "../../libs/style";

type MapStyle = StyleSpecification & { id: string };

type StyleGalleryProps = {
  onOpenStyle(mapStyle: MapStyle): void;
};

// The preset styles to start from, as pictures. The empty style is left
// out: it is what the user is looking at already.
const PRESETS = publicStyles.filter((preset) => preset.id !== "0-empty-style");

export default function StyleGallery(props: StyleGalleryProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  // Loaded the way the Open dialog loads a preset.
  const open = async (preset: (typeof PRESETS)[number]) => {
    setLoading(preset.id);
    setFailed(null);
    try {
      const response = await fetch(preset.url, { mode: "cors", credentials: "same-origin" });
      const body = await response.json();
      props.onOpenStyle(style.ensureStyleValidity(body));
    } catch (err) {
      setFailed(preset.title);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="studio-gallery" data-wd-key="simple:presets">
      <ul className="studio-gallery__grid">
        {PRESETS.map((preset) => (
          <li key={preset.id}>
            <button
              type="button"
              className="studio-gallery__card"
              data-wd-key={`simple:preset:${preset.id}`}
              disabled={loading !== null}
              aria-busy={loading === preset.id}
              onClick={() => open(preset)}
            >
              <img
                className="studio-gallery__picture"
                src={preset.thumbnail}
                alt=""
                loading="lazy"
              />
              <span className="studio-gallery__name">
                {loading === preset.id ? t("Loading…") : preset.title}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {failed && (
        <p className="studio-gallery__error" role="alert">
          {t("Could not load {{name}}. Try again, or pick another style.", {
            name: failed,
          })}
        </p>
      )}
    </div>
  );
}
